import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation, useParams } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'

import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import './AumhumSite.css'
import { Footer, TopBar } from './Shell'
import { CAREGIVERS, DOCTORS } from './roleData'
import CareStartView from './CareStartView'
import {
  CareEnvironments,
  CareModel,
  Conditions,
  Container,
  FinalCTA,
  GetStartedSection,
  GuidedCare,
  Hero,
  HowEndocrine,
  HowFaq,
  HowHero,
  HowInfrastructure,
  HowPathway,
  HowTeam,
  HowValue,
  Impact,
  RealWorldScenarios,
  RolePage,
  SelfCareCallout,
  StudentsProgram,
  TrustStrip,
  UnderstandingMH,
  WhatWeDo,
  WhyAumhumSummary,
  WorkWithAumhum,
} from './sections'

const CANONICAL_ORIGIN = 'https://aumhum.com'

// ── Views ─────────────────────────────────────────────────

// Home is decision-first: hero → trust → choose a starting point → what-we-do
// → guided-care → real scenarios → conditions → care model → biology context
// → care environments → self-care app → work-with-AUMHUM → why → impact → CTA.
const HomeView = () => (
  <main id="main">
    <Hero />
    <TrustStrip />
    <GetStartedSection />
    <WhatWeDo />
    <GuidedCare />
    <RealWorldScenarios />
    <Conditions />
    <CareModel />
    <UnderstandingMH />
    <CareEnvironments />
    <SelfCareCallout />
    <WorkWithAumhum />
    <WhyAumhumSummary />
    <Impact />
    <FinalCTA />
  </main>
)

const HowCareWorksView = () => (
  <main id="main">
    <HowHero />
    <HowPathway />
    <HowTeam />
    <HowEndocrine />
    <HowInfrastructure />
    <HowValue />
    <HowFaq />
    <FinalCTA />
  </main>
)

const StudentsView = () => (
  <main id="main">
    <StudentsProgram />
  </main>
)

const CareStartPageView = () => <CareStartView />

const DoctorView = () => {
  const { slug } = useParams()
  const cfg = DOCTORS.find((d) => d.slug === slug)
  if (!cfg) return <NotFoundView />
  return (
    <main id="main">
      <RolePage config={cfg} />
    </main>
  )
}

const CaregiverView = () => {
  const { slug } = useParams()
  const cfg = CAREGIVERS.find((c) => c.slug === slug)
  if (!cfg) return <NotFoundView />
  return (
    <main id="main">
      <RolePage config={cfg} />
    </main>
  )
}

const NotFoundView = () => (
  <main id="main" className="as-section as-section-alt">
    <Container>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="as-max-w-3">
        <h1 className="as-h2">This page could not be found.</h1>
        <p className="as-lede">
          Try starting from the homepage, or explore how care works at AUMHUM.
        </p>
        <div className="as-mt-6 as-flex as-gap-3 as-wrap">
          <Link to="/" className="as-btn as-btn-primary">
            Go to home
          </Link>
          <Link to="/how-care-works" className="as-btn as-btn-secondary">
            How care works
          </Link>
        </div>
      </div>
    </Container>
  </main>
)

// ── SEO ───────────────────────────────────────────────────

const DEFAULT_ORGANIZATION_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  name: 'AUMHUM',
  url: CANONICAL_ORIGIN,
  logo: APP_CONSTANTS.APP_LOGO_LINK,
  sameAs: [APP_CONSTANTS.PLAY_STORE_APP_LINK, APP_CONSTANTS.APP_STORE_APP_LINK].filter(Boolean),
  medicalSpecialty: ['Psychiatry', 'Psychology', 'Endocrinology'],
  areaServed: ['IN', 'US', 'AE'],
  availableLanguage: ['en', 'hi'],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: APP_CONSTANTS.SUPPORT_EMAIL,
      availableLanguage: ['en', 'hi'],
    },
  ],
}

function buildJsonLd(view, pathname, config) {
  const url = `${CANONICAL_ORIGIN}${pathname}`
  const base = {
    '@context': 'https://schema.org',
  }
  if (view === 'how-care-works') {
    return [
      DEFAULT_ORGANIZATION_JSONLD,
      {
        ...base,
        '@type': 'MedicalWebPage',
        name: 'How care works at AUMHUM',
        url,
        about:
          'Continuous clinical care model integrating therapy, psychiatry, and endocrinology.',
        audience: { '@type': 'PeopleAudience', audienceType: 'patients' },
      },
      {
        ...base,
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Do I have to see a psychiatrist or an endocrinologist?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'These specialists are brought into your plan only when clinically indicated. Many people are supported entirely by therapy.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is this remote or in-person?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Both. Care is delivered across outpatient clinics, rehabilitation settings, and secure teleconsultation.',
            },
          },
          {
            '@type': 'Question',
            name: 'How is my clinical data protected?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Clinical data is protected under documented privacy and governance standards. Access is restricted to your care team.',
            },
          },
        ],
      },
    ]
  }
  if (view === 'students') {
    return [
      DEFAULT_ORGANIZATION_JSONLD,
      {
        ...base,
        '@type': 'EducationalOccupationalProgram',
        name: 'AUMHUM Experience Program',
        description:
          'A 6-month clinical placement program for psychology students — real patient exposure, supervision, and rotation across AUMHUM clinical settings.',
        timeToComplete: 'P6M',
        provider: { '@type': 'Organization', name: 'AUMHUM', url: CANONICAL_ORIGIN },
        url,
      },
    ]
  }
  if (view === 'care-start') {
    return [
      DEFAULT_ORGANIZATION_JSONLD,
      {
        ...base,
        '@type': 'MedicalWebPage',
        name: 'Start care at AUMHUM',
        url,
        about:
          'Guided care intake across portal, WhatsApp, and callback-based assistance.',
        audience: { '@type': 'PeopleAudience', audienceType: 'patients' },
      },
    ]
  }
  if (view === 'doctor' || view === 'caregiver') {
    return [
      DEFAULT_ORGANIZATION_JSONLD,
      {
        ...base,
        '@type': 'JobPosting',
        title: config?.title,
        description: config?.intro,
        hiringOrganization: {
          '@type': 'Organization',
          name: 'AUMHUM',
          sameAs: CANONICAL_ORIGIN,
        },
        employmentType: 'FULL_TIME',
        jobLocationType: 'TELECOMMUTE',
        url,
      },
    ]
  }
  return [
    DEFAULT_ORGANIZATION_JSONLD,
    {
      ...base,
      '@type': 'WebSite',
      name: 'AUMHUM',
      url: CANONICAL_ORIGIN,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${CANONICAL_ORIGIN}/therapy-psychiatry?search={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ]
}

const AumhumHelmet = ({ view, pathname, config }) => {
  const meta = useMemo(() => {
    if (view === 'how-care-works') {
      return {
        title: 'How care works at AUMHUM — continuous clinical mental healthcare',
        description:
          'See how AUMHUM delivers continuous care: clinical intake, assessment, therapy, psychiatric and endocrine review, measurement, and ongoing continuity.',
      }
    }
    if (view === 'students') {
      return {
        title: 'AUMHUM Experience Program — clinical placement for psychology students',
        description:
          '6-month supervised clinical placement for undergraduate, post-graduate, MPhil, and PhD psychology students at AUMHUM.',
      }
    }
    if (view === 'care-start') {
      return {
        title: 'Start care at AUMHUM — portal, WhatsApp, or callback',
        description:
          'Choose portal, WhatsApp, or phone callback to begin a guided care intake and get routed to the right next step.',
      }
    }
    if ((view === 'doctor' || view === 'caregiver') && config) {
      return {
        title: `${config.title} at AUMHUM — ${config.tagline}`,
        description: config.intro,
      }
    }
    return {
      title: 'AUMHUM — Clinical mental healthcare, delivered as continuous care',
      description:
        'Therapy, psychiatric consultations, and endocrine review — coordinated by a single clinical team. A continuous care system, not a one-off session.',
    }
  }, [view, config])

  const canonical = `${CANONICAL_ORIGIN}${pathname}`
  const image = APP_CONSTANTS.APP_LOGO_LINK
  const jsonLd = useMemo(
    () => buildJsonLd(view, pathname, config),
    [view, pathname, config],
  )

  return (
    <Helmet>
      <html lang="en" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta
        name="keywords"
        content="mental healthcare, psychiatry, therapy, endocrinology, clinical mental health, AUMHUM, continuous care, psychotherapy, India, mental health clinic"
      />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <link rel="preconnect" href="https://media.aumhum.com" crossOrigin="" />
      <link rel="dns-prefetch" href="https://media.aumhum.com" />

      <meta property="og:site_name" content="AUMHUM" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />

      <meta name="theme-color" content="#ffffff" media="(prefers-color-scheme: light)" />
      <meta name="theme-color" content="#0f172a" media="(prefers-color-scheme: dark)" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />

      {jsonLd.map((obj, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  )
}

// ── Root ──────────────────────────────────────────────────

// Drives the fixed scroll-progress bar at the top of the site and the
// scroll-reveal entrance animation for elements tagged with `.as-reveal`.
// Kept here (root level) so it attaches once per route — cheaper than
// each section registering its own observer.
function useScrollProgressAndReveal(rootRef) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const root = rootRef.current
    if (!root) return undefined
    const progressEl = root.querySelector('.as-scroll-progress')

    let rafId = 0
    let queued = false
    const updateProgress = () => {
      queued = false
      const docEl = document.documentElement
      const scrollable = Math.max(1, docEl.scrollHeight - docEl.clientHeight)
      const ratio = Math.min(1, Math.max(0, window.scrollY / scrollable))
      if (progressEl) {
        progressEl.style.setProperty('--as-scroll-progress', ratio.toFixed(4))
        progressEl.setAttribute('data-active', window.scrollY > 16 ? 'true' : 'false')
      }
    }
    const onScroll = () => {
      if (queued) return
      queued = true
      rafId = window.requestAnimationFrame(updateProgress)
    }
    updateProgress()
    window.addEventListener('scroll', onScroll, { passive: true })

    // Scroll-reveal via IntersectionObserver. Once an element enters
    // the viewport it stays revealed; we stop observing it to keep
    // the observer cheap.
    let observer
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in-view')
              observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.08, rootMargin: '0px 0px -5% 0px' },
      )
      root.querySelectorAll('.as-reveal').forEach((el) => observer.observe(el))
    } else {
      // No-IO fallback: just show them all.
      root.querySelectorAll('.as-reveal').forEach((el) => el.classList.add('is-in-view'))
    }

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafId) window.cancelAnimationFrame(rafId)
      if (observer) observer.disconnect()
    }
  }, [rootRef])
}

export default function AumhumSite({ view = 'home' }) {
  const location = useLocation()
  const { slug } = useParams()
  const pathname = location.pathname
  const rootRef = React.useRef(null)

  const [authOpen, setAuthOpen] = useState(false)
  const [authVariant, setAuthVariant] = useState('login')

  const config = useMemo(() => {
    if (view === 'doctor') return DOCTORS.find((d) => d.slug === slug)
    if (view === 'caregiver') return CAREGIVERS.find((c) => c.slug === slug)
    return null
  }, [view, slug])

  useScrollProgressAndReveal(rootRef)

  // Open auth modal automatically when arriving with redirect/login query params
  // (parity with PublicAppHomepage behavior).
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.has('redirect') || params.get('lt')) {
      setAuthVariant('login')
      setAuthOpen(true)
    }
  }, [location.search])

  // Scroll to top on view change (hash-less navigation)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (location.hash) return
    window.scrollTo(0, 0)
  }, [location.pathname, location.hash])

  let viewEl = <HomeView />
  if (view === 'how-care-works') viewEl = <HowCareWorksView />
  else if (view === 'students') viewEl = <StudentsView />
  else if (view === 'care-start') viewEl = <CareStartPageView />
  else if (view === 'doctor') viewEl = <DoctorView />
  else if (view === 'caregiver') viewEl = <CaregiverView />

  const urlParams = new URLSearchParams(location.search)
  const redirectUri = urlParams.get('redirect')

  return (
    <div className="aumhum-site-root" ref={rootRef}>
      <AumhumHelmet view={view} pathname={pathname} config={config} />
      <div className="as-scroll-progress" aria-hidden="true" />
      <a href="#main" className="as-skip-link">
        Skip to content
      </a>
      <TopBar
        onLogin={() => {
          setAuthVariant('login')
          setAuthOpen(true)
        }}
      />
      {viewEl}
      <Footer />
      <ClubbedAuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        loginVariant={authVariant === 'login'}
        loginFieldHeading={redirectUri ? 'Please login to continue' : undefined}
        redirectToURI={redirectUri}
      />
    </div>
  )
}
